import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Smile, Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const EMOJIS = [
  "😀","😁","😂","🤣","😊","😍","😘","😎","🤩","🥳","😉","🙂","🙃","😌","😴","🤔",
  "🤗","🤝","👏","🙌","👍","👎","👊","✊","🤙","🙏","💪","🫶","👀","🎉","🔥","✨",
  "💯","⭐","🌟","💡","🚀","📈","💼","🎯","✅","❤️","🧡","💛","💚","💙","💜","🖤",
  "😅","😇","😭","😢","😤","😳","🥹","😬","🤯","😱","👋","🤞","💥","☕","🍻","🫡",
];

function EmojiGrid({ onEmoji }) {
  return (
    <div className="grid grid-cols-8 gap-1 p-3 max-h-64 overflow-y-auto no-scrollbar">
      {EMOJIS.map((e, i) => (
        <button key={e} type="button" data-testid={`emoji-${i}`} onClick={() => onEmoji(e)}
          className="text-2xl leading-none p-1 rounded-md hover:bg-surface-3">{e}</button>
      ))}
    </div>
  );
}

function GifResults({ loading, configured, gifs, onGif }) {
  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-brand" /></div>;
  if (!configured) return <p className="text-muted-foreground text-xs text-center py-8">GIF search isn&apos;t set up yet. Add a GIPHY API key to enable it.</p>;
  if (gifs.length === 0) return <p className="text-muted-foreground text-xs text-center py-8">No GIFs found.</p>;
  return (
    <div className="columns-2 gap-2">
      {gifs.map((g) => (
        <button key={g.id} type="button" data-testid={`gif-${g.id}`} onClick={() => onGif(g)}
          className="mb-2 w-full rounded-lg overflow-hidden block">
          <img src={g.preview_url || g.media_url} alt={g.alt_text} className="w-full rounded-lg" loading="lazy" />
        </button>
      ))}
    </div>
  );
}

function GifPanel({ onGif }) {
  const [q, setQ] = useState("");
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState(true);
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const path = q.trim() ? `/gifs/search?q=${encodeURIComponent(q.trim())}` : "/gifs/trending";
        const res = await api(path);
        setConfigured(res.configured !== false);
        setGifs(res.data || []);
      } catch { setGifs([]); } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="p-3">
      <div className="flex items-center gap-2 bg-surface border border-line rounded-pill px-3 py-2 mb-2">
        <Search className="w-4 h-4 text-muted-foreground" />
        <input data-testid="gif-search" value={q} onChange={(e) => setQ(e.target.value.slice(0, 50))}
          placeholder="Search GIFs" className="flex-1 bg-transparent outline-none text-sm" />
      </div>
      <div className="max-h-56 overflow-y-auto no-scrollbar">
        <GifResults loading={loading} configured={configured} gifs={gifs} onGif={onGif} />
      </div>
      <p className="text-[10px] text-muted-foreground text-center mt-2">Powered by GIPHY</p>
    </div>
  );
}

function PickerTab({ id, label, active, onClick }) {
  return (
    <button type="button" data-testid={`picker-tab-${id}`} onClick={() => onClick(id)}
      className={`flex-1 py-2.5 text-sm ${active ? "text-brand border-b-2 border-brand font-medium" : "text-muted-foreground"}`}>{label}</button>
  );
}

export default forwardRef(function EmojiGifPicker({ onEmoji, onGif }, ref) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("emoji");
  const panelRef = useRef(null);

  useImperativeHandle(ref, () => ({ close: () => setOpen(false) }), []);

  useEffect(() => {
    const onDoc = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pickGif = (g) => { onGif(g); setOpen(false); };

  return (
    <div className="relative" ref={panelRef}>
      <button type="button" data-testid="chat-emoji-btn" onClick={() => setOpen((o) => !o)}
        className="w-11 h-11 rounded-full bg-surface-2 border border-line text-ink flex items-center justify-center hover:bg-surface-3 shrink-0">
        <Smile className="w-5 h-5" />
      </button>
      {open && (
        <div data-testid="emoji-gif-panel" className="absolute bottom-full mb-2 left-0 z-50 w-[320px] bg-surface-2 border border-line rounded-xl shadow-xl overflow-hidden">
          <div className="flex border-b border-line">
            <PickerTab id="emoji" label="Emoji" active={tab === "emoji"} onClick={setTab} />
            <PickerTab id="gif" label="GIFs" active={tab === "gif"} onClick={setTab} />
          </div>
          {tab === "emoji" ? <EmojiGrid onEmoji={onEmoji} /> : <GifPanel onGif={pickGif} />}
        </div>
      )}
    </div>
  );
});
